// `browser.fromPixels` / `browser.toPixels` - NOT top-level exports of @tensorflow/tfjs-core@4.
// Imported by bare name they resolve to undefined, so calling them throws. (Currently unreachable
// because _tfModel is never assigned, but it would fire the moment segmentation is restored.)
import { browser } from '@tensorflow/tfjs-core';

import { VIRTUAL_BACKGROUND_TYPE } from '../../virtual-background/constants';
import logger from '../../virtual-background/logger';
import { CROP_EPSILON, getCropRect, getSoftwareZoom } from '../camera-zoom/CaptureZoomState';

import {
    CLEAR_TIMEOUT,
    SET_TIMEOUT,
    TIMEOUT_TICK,
    timerWorkerScript
} from './TimerWorker';
import BatteryMonitor from './batteryMonitor';
import { DEVICE_CAPABILITY, getDeviceCapability, getQualitySettings } from './deviceCapabilities';
import ResourceMonitor from './resourceMonitor';

/**
 * Frame-rate cap while the conference is out of sight, see _getAdaptiveFrameDelay().
 *
 * @type {number}
 */
const HIDDEN_MAX_FPS = 15;

/**
 * Represents a modified MediaStream that adds effects to video background.
 * <tt>MHStreamBackgroundEffect</tt> does the processing of the original
 * video stream.
 *
 * This version implements a soft-matting pipeline (E2 – balanced edges):
 *  1. Pre-blur + gamma-correct input before inference.
 *  2. Read model probabilities → build SOFT alpha (0–1), no hard 0/255.
 *  3. Run a guided (edge-aware) smoothing on the alpha mask using the
 *     video frame as guidance (approx joint bilateral filter).
 *  4. Light Gaussian blur on mask only (2px).
 *  5. Temporal EMA smoothing for stability.
 *  6. Composite foreground and background with soft edges.
 */
export default class MHStreamBackgroundEffect {
    /**
     * Represents a modified video MediaStream track.
     *
     * @class
     * @param {Object} model - Meet model.
     * @param {Object} options - Segmentation dimensions.
     * @param {number} options.height - Height of the segmentation.
     * @param {number} options.width - Width of the segmentation.
     * @param {Object} options.virtualBackground - Virtual background configuration.
     * @param {string} [options.virtualBackground.backgroundType] - Type of background.
     * @param {number} [options.virtualBackground.blurValue] - Blur value for background.
     * @param {string} [options.virtualBackground.virtualSource] - Source for virtual background.
     */
    constructor(model, options) {
        this._options = options;
        this._qualitySettings = options.qualitySettings || {};
        this._performanceMonitor = options.performanceMonitor || null;
        this._capabilityUsed = options.capabilityUsed || 'medium';

        if (this._options.virtualBackground.backgroundType === VIRTUAL_BACKGROUND_TYPE.IMAGE) {
            this._virtualImage = document.createElement('img');
            this._virtualImage.crossOrigin = 'anonymous';
            this._virtualImage.src = this._options.virtualBackground.virtualSource || '';
        }

        this._model = model;
        this._segmentationPixelCount = this._options.width * this._options.height;

        // Bind event handler so it is only bound once for every instance.
        this._onMaskFrameTimer = this._onMaskFrameTimer.bind(this);

        // Workaround for FF issue https://bugzilla.mozilla.org/show_bug.cgi?id=1388974
        this._outputCanvasElement = document.createElement('canvas');
        this._outputCanvasElement.getContext('2d');
        this._inputVideoElement = document.createElement('video');

        // Capture zoom: the camera frame is cropped here, before segmentation,
        // exactly like the native pipeline zooms before virtual background.
        // While no crop is needed the video element is drawn directly.
        this._zoomCanvasElement = document.createElement('canvas');
        this._zoomCanvasCtx = null;
        this._frameSource = this._inputVideoElement;

        // Temporal mask smoothing state (previous alpha ImageData).
        this._prevMaskImageData = null;

        // Guidance frame (RGB at segmentation resolution) for guided filtering.
        this._guideImageData = null;

        // Frame rate adaptation
        this._deviceCapability = getDeviceCapability();
        this._currentTargetFps = this._qualitySettings.targetFps || 30;
        this._frameStartTime = 0;
        this._droppedFrameCount = 0;
        this._processedFrameCount = 0;
        this._lastMonitorLogTime = 0;
        this._monitorLogInterval = 10000; // Log performance metrics every 10 seconds

        // Auto-center zoom: smoothed crop that keeps the segmented person centred.
        // Gated per frame on this._options.virtualBackground.autoCenterZoom, so it
        // only runs with a virtual background on and the user setting enabled.
        // `enabled` is left for _updateQualitySettings() to poke harmlessly.
        this._smartZoom = {
            enabled: this._qualitySettings.enableSmartZoom || false,
            maxZoom: 2.2, // never upscale past this - keeps the picture from going soft
            targetFill: 0.8, // fraction of the frame the subject should fill
            smoothness: 0.15, // EMA factor per frame (lower = steadier, more lag)
            currentZoom: 1,
            centerX: 0.5,
            centerY: 0.5
        };

        this._videoEnhancer = {
            enabled: this._qualitySettings.enableVideoEnhancer || false,
            brightness: 0.5,
            contrast: 0,
            saturation: 1,
            noiseReduction: 0.3
        };

        // Phase 2: Dynamic resource monitoring
        this._resourceMonitor = new ResourceMonitor({
            checkInterval: 30000, // 30 seconds
            onCapabilityChange: data => this._onResourceCapabilityChange(data)
        });

        // Phase 3: Dynamic battery monitoring
        this._batteryMonitor = new BatteryMonitor({
            criticalBatteryLevel: 0.2, // 20%
            onBatteryAdapt: data => this._onBatteryAdapt(data)
        });
    }

    /**
     * EventHandler onmessage for the maskFrameTimerWorker WebWorker.
     *
     * @private
     * @param {Object} response - The onmessage EventHandler parameter.
     * @param {Object} response.data - The data from the worker.
     * @param {number} response.data.id - The message ID.
     * @returns {void}
     */
    _onMaskFrameTimer(response) {
        // Only the current worker drives the loop. A tick still pending on a worker that startEffect() replaced
        // would otherwise run a frame that re-schedules on the current worker - which already has a tick of its
        // own - and from then on two render loops share one effect, each running the full per-pixel pipeline.
        if (response.target && response.target !== this._maskFrameTimerWorker) {
            return;
        }

        if (response.data.id === TIMEOUT_TICK) {
            this._renderMask();
        }
    }

    /**
     * Run Inference from the TF Lite model
     *
     * SOFT MATTING PIPELINE:
     *   - Read probabilities from model.
     *   - Build soft alpha [0..1] using low/high thresholds (trimap).
     *   - Guided filter: edge-aware smoothing using guide RGB frame.
     *   - Slight Gaussian blur on mask only.
     *   - Temporal EMA smoothing (alpha).
     */
    runInference() {
        this._model._runInference();
        const offset = this._model._getOutputMemoryOffset() / 4;

        // 1. Build soft alpha [0..1] directly from the model probabilities.
        //    The selfie-segmentation model outputs a per-pixel sigmoid probability in
        //    [0..1], so the trimap thresholds are FIXED absolute values. The previous
        //    version scaled them by the per-frame max probability, which drifts with
        //    lighting from frame to frame and made the whole edge wobble/flicker even
        //    when the subject held still. Fixed thresholds remove that coupling.
        const FG_LOW = 0.40; // below this → background
        const FG_HIGH = 0.65; // above this → confident foreground (narrow ramp = crisper edge)

        let softMask = new ImageData(this._options.width, this._options.height);
        const alphaArr = softMask.data;

        for (let i = 0; i < this._segmentationPixelCount; i++) {
            const v = this._model.HEAPF32[offset + i];

            let a;

            if (v <= FG_LOW) {
                a = 0;
            } else if (v >= FG_HIGH) {
                a = 1;
            } else {
                a = (v - FG_LOW) / (FG_HIGH - FG_LOW);
            }

            alphaArr[(i * 4) + 3] = Math.max(0, Math.min(1, a)) * 255;
        }

        // 2. Keep ONLY the person closest to the camera. The model segments every
        //    person in view, so a second person standing behind leaks into the matte.
        //    Label connected foreground regions and keep the single largest one - the
        //    nearest subject fills the most pixels. Done on the crisp (pre-smoothing)
        //    mask so removed regions cannot bleed back in during the smoothing steps.
        keepLargestConnectedComponent(softMask, this._options.width, this._options.height);

        // 3. Guided edge-aware smoothing on alpha, using the guidance RGB frame.
        //    Snaps the matte edge onto the real person boundary (hands, shirt).
        //    Skip on low-end devices to improve performance.
        if (this._guideImageData && this._qualitySettings.useGuidedFilter) {
            const radius = this._qualitySettings.guidedFilterRadius || 2;

            softMask = guidedFilterMask({
                maskImage: softMask,
                guideImage: this._guideImageData,
                width: this._options.width,
                height: this._options.height,
                radius,
                sigmaSpatial: 2.0, // sigmaSpatial
                sigmaRange: 0.10 // sigmaRange (luminance)
            });
        }

        // 4. Light Gaussian blur on mask only - just enough to de-stair the edge.
        //    Kept small (2px) so edges stay sharp instead of a wide feather.
        const blurRadius = this._qualitySettings.blurRadius || 1;

        softMask = blurMaskEdges(softMask, this._options.width, this._options.height, blurRadius);

        // 5. Temporal smoothing over time (EMA) to reduce flicker. Weighting the
        //    previous frame more heavily trades a little motion lag for a much
        //    steadier edge. Skip on very low-end devices to save CPU.
        if (this._qualitySettings.enableTemporalSmoothing && this._prevMaskImageData) {
            const temporalAlpha = this._qualitySettings.temporalAlpha || 0.6;

            softMask = smoothMaskOverTime(softMask, this._prevMaskImageData, temporalAlpha);
        }
        this._prevMaskImageData = softMask;

        // 6. Keep as segmentation mask used for compositing.
        this._segmentationMask = softMask;
        this._segmentationMaskCtx.putImageData(this._segmentationMask, 0, 0);
    }

    /**
     * Resize source frame → Write into tflite input tensor
     * ★ Enhancements integrated: mild blur + gamma + normalization pre-processing.
     *
     * Also captures a guidance RGB image at segmentation resolution
     * for the guided filter step in runInference().
     */
    resizeSource() {
        // Pre-blur input frame slightly to reduce high-frequency noise.
        this._segmentationMaskCtx.filter = 'blur(1px)';
        this._segmentationMaskCtx.drawImage(
            this._frameSource,
            0, 0,
            this._inputVideoElement.width,
            this._inputVideoElement.height,
            0, 0,
            this._options.width,
            this._options.height
        );
        this._segmentationMaskCtx.filter = 'none';

        const imageData = this._segmentationMaskCtx.getImageData(
            0, 0,
            this._options.width,
            this._options.height
        );

        const data = imageData.data;

        // Save guidance image BEFORE any normalization for guided filter.
        this._guideImageData = new ImageData(
            new Uint8ClampedArray(data), // copy
            this._options.width,
            this._options.height
        );

        // Mild gamma correction / tone normalization.
        for (let i = 0; i < data.length; i += 4) {
            const gamma = 1.05;

            data[i] = 255 * Math.pow(data[i] / 255, gamma);
            data[i + 1] = 255 * Math.pow(data[i + 1] / 255, gamma);
            data[i + 2] = 255 * Math.pow(data[i + 2] / 255, gamma);
        }

        const inputOffset = this._model._getInputMemoryOffset() / 4;

        // Normalized RGB → tflite tensor.
        for (let i = 0; i < this._segmentationPixelCount; i++) {
            this._model.HEAPF32[inputOffset + (i * 3)] = imageData.data[i * 4] / 255;
            this._model.HEAPF32[inputOffset + (i * 3) + 1] = imageData.data[(i * 4) + 1] / 255;
            this._model.HEAPF32[inputOffset + (i * 3) + 2] = imageData.data[(i * 4) + 2] / 255;
        }
    }

    /**
     * Composite foreground and background.
     * Foreground is NEVER blurred here; only mask & background are.
     */
    runPostProcessing() {
        const track = this._stream.getVideoTracks()[0];
        const settings = track.getSettings ? track.getSettings() : track.getConstraints();
        const { height, width } = settings;
        const { backgroundType } = this._options.virtualBackground;

        if (!this._outputCanvasCtx) {
            return;
        }

        this._outputCanvasElement.height = height;
        this._outputCanvasElement.width = width;

        // 1. Start from alpha mask. The mask is upscaled from 256x144 to the full
        //    output size, so a small blur hides the upscale stair-stepping. Kept low
        //    (2-3px) so the edge reads sharp rather than a wide halo - the old 4/8px
        //    was the main cause of the fuzzy outline.
        this._outputCanvasCtx.globalCompositeOperation = 'copy';
        this._outputCanvasCtx.filter = backgroundType === VIRTUAL_BACKGROUND_TYPE.IMAGE ? 'blur(1px)' : 'blur(2px)';
        this._outputCanvasCtx.drawImage(
            this._segmentationMaskCanvas,
            0, 0,
            this._options.width,
            this._options.height,
            0, 0,
            this._inputVideoElement.width,
            this._inputVideoElement.height
        );

        // 2. Use mask as alpha to keep foreground only.
        this._outputCanvasCtx.globalCompositeOperation = 'source-in';
        this._outputCanvasCtx.filter = 'none';
        this._outputCanvasCtx.drawImage(this._frameSource, 0, 0);

        // 3. Draw background underneath.
        this._outputCanvasCtx.globalCompositeOperation = 'destination-over';

        if (backgroundType === VIRTUAL_BACKGROUND_TYPE.IMAGE) {
            // Static image background (no additional blur).
            this._outputCanvasCtx.filter = 'none';
            this._outputCanvasCtx.drawImage(
                backgroundType === VIRTUAL_BACKGROUND_TYPE.IMAGE
                    ? this._virtualImage : this._virtualVideo,
                 0,
                0,
                this._outputCanvasElement.width,
                this._outputCanvasElement.height
            );
        } else {
            // Blur the original camera as background.
            this._outputCanvasCtx.filter = `blur(${this._options.virtualBackground.blurValue}px)`;
            this._outputCanvasCtx.drawImage(this._frameSource, 0, 0);
        }
    }

    /**
     * Perform inference using TensorFlow.js.
     *
     * @param {ImageData} inputFrame - The input video frame as ImageData.
     * @returns {ImageData} - The processed frame with improved virtual background quality.
     */
    async _applyInference(inputFrame) {
        if (!this._tfModel) {
            // TensorFlow.js model is not initialized. Skipping inference.

            return inputFrame;
        }

        try {
            const inputTensor = browser.fromPixels(inputFrame);
            const normalizedTensor = inputTensor.div(255.0).expandDims(0);
            const outputTensor = this._tfModel.predict(normalizedTensor);

            const outputData = await browser.toPixels(outputTensor.squeeze());
            const outputImageData = new ImageData(
                new Uint8ClampedArray(outputData),
                inputFrame.width,
                inputFrame.height
            );

            inputTensor.dispose();
            normalizedTensor.dispose();
            outputTensor.dispose();

            return outputImageData;
        } catch {
            // TensorFlow.js inference failed.

            return inputFrame;
        }
    }

    /**
     * Measure frame processing time and adapt frame rate if needed.
     * Returns the time (in ms) to wait before processing next frame.
     * Records timing data for performance monitoring.
     *
     * @private
     * @returns {number} Time in ms until next frame should be processed
     */
    _getAdaptiveFrameDelay() {
        // Measure frame time
        const frameTime = this._frameStartTime ? performance.now() - this._frameStartTime : 0;

        // Record in performance monitor
        if (this._performanceMonitor && frameTime > 0) {
            this._performanceMonitor.recordFrameTime(frameTime);
        }

        // Get frame budget based on target FPS
        const frameBudgetMs = 1000 / this._currentTargetFps;

        // If frame took too long, reduce FPS
        if (frameTime > frameBudgetMs * 1.2) {
            this._droppedFrameCount++;

            // Only reduce FPS for low-capability devices
            if (this._deviceCapability !== DEVICE_CAPABILITY.HIGH) {
                if (this._currentTargetFps > 10) {
                    this._currentTargetFps = Math.max(10, this._currentTargetFps - 5);
                }
            }
        } else if (frameTime < frameBudgetMs * 0.7 && this._droppedFrameCount === 0) {
            // Recover to higher FPS if we're consistently fast
            if (this._currentTargetFps < 30) {
                this._currentTargetFps = Math.min(30, this._currentTargetFps + 2);
            }
        }

        // Log performance metrics periodically
        const nowTime = performance.now();

        if (nowTime - this._lastMonitorLogTime > this._monitorLogInterval && this._performanceMonitor) {
            this._lastMonitorLogTime = nowTime;
            const summary = this._performanceMonitor.formatPerformanceSummary();

            logger.debug(`[VirtualBackground] Performance: ${summary}`);
        }

        // Nobody can see the conference (hidden page, no Picture-in-Picture window): others still get the camera,
        // but at a lower rate. Each frame runs a per-pixel filter on the main thread, and while the OS throttles a
        // background browser (Windows Energy Saver) that work starves audio playback in the same process. Applied
        // here only, so _currentTargetFps keeps adapting and full rate returns as soon as the page is visible.
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden'
                && !window.documentPictureInPicture?.window) {
            return 1000 / Math.min(this._currentTargetFps, HIDDEN_MAX_FPS);
        }

        return 1000 / this._currentTargetFps;
    }

    /**
     * Loop function to render the background mask.
     *
     * @private
     * @returns {void}
     */
    async _renderMask() {
        // The re-schedule below MUST NOT be reachable only on the success path.
        //
        // This is the whole render loop: each frame ends by asking the worker for the next tick. As the
        // last statement of the happy path, a single throw anywhere above it - a lost WebGL context, a
        // tfjs allocation failure, a canvas op failing under GPU pressure - skipped the postMessage, so
        // the worker was never asked for another tick and the loop was dead for the rest of the call.
        // _onMaskFrameTimer neither awaits nor catches this promise, so nothing surfaced either:
        // captureStream() keeps handing out a track that is still "live", it just stops producing
        // frames. Draining one bad frame is always better than ending the stream.
        //
        // Capture the worker this frame belongs to. MHLocalTrack keeps `_streamEffect` across a mute and
        // restarts the SAME instance on unmute, so stopEffect()/startEffect() can run while this frame is
        // suspended at the await - leaving `_running` true again but with a DIFFERENT worker that already
        // has its own pending tick. Re-scheduling on the current reference would run two loops at once,
        // compounding with every mute/unmute cycle.
        const timerWorker = this._maskFrameTimerWorker;

        this._frameStartTime = performance.now();
        this._processedFrameCount++;

        try {
            this._frameSource = this._zoomedInput(); // ⬅ capture zoom crop (camera-app zoom)
            this.resizeSource(); // ⬅ pre-processing + guidance frame
            this.runInference(); // ⬅ soft matting + guided edges + EMA

            await this._applyInference(this._segmentationMask);

            // Use the processed frame for further rendering
            this.runPostProcessing(); // ⬅ composite

            // Apply smart zoom and video enhancements (if enabled in quality settings)
            this._applyEnhancements();
        } catch (error) {
            // Throttled: a persistently failing frame would otherwise log at the full frame rate.
            const now = Date.now();

            if (!this._lastRenderErrorLog || now - this._lastRenderErrorLog > 10000) {
                this._lastRenderErrorLog = now;
                logger.warn('Virtual background frame failed; dropping it and continuing', error);
            }
        } finally {
            // Do not resurrect a loop that stopEffect() tore down, and do not tick a replacement worker.
            if (this._running && timerWorker && this._maskFrameTimerWorker === timerWorker) {
                timerWorker.postMessage({ id: SET_TIMEOUT,
                    timeMs: this._getAdaptiveFrameDelay() });
            }
        }
    }

    /**
     * Returns the element to read this frame from: the input video itself, or
     * a canvas holding its centered crop when the local user's capture zoom
     * needs more than the camera hardware provided. Segmentation, foreground
     * and blurred background all read the same source, so the whole effect
     * sees the zoomed picture and remote participants get the same framing.
     *
     * @private
     * @returns {HTMLVideoElement|HTMLCanvasElement}
     */
    _zoomedInput() {
        const video = this._inputVideoElement;
        const zoom = getSoftwareZoom('camera');

        if (zoom <= 1 + CROP_EPSILON) {
            return video;
        }

        const width = video.videoWidth || video.width;
        const height = video.videoHeight || video.height;

        if (!(width > 0) || !(height > 0)) {
            return video;
        }

        if (!this._zoomCanvasCtx) {
            this._zoomCanvasCtx = this._zoomCanvasElement.getContext('2d');
        }
        if (this._zoomCanvasElement.width !== width || this._zoomCanvasElement.height !== height) {
            this._zoomCanvasElement.width = width;
            this._zoomCanvasElement.height = height;
        }

        const { sx, sy, sw, sh } = getCropRect(width, height, zoom);

        this._zoomCanvasCtx.drawImage(video, sx, sy, sw, sh, 0, 0, width, height);

        return this._zoomCanvasElement;
    }

    /**
     * Apply smart zoom and video enhancements to the output frame.
     * Controlled by quality settings based on device capability.
     *
     * @private
     * @returns {void}
     */
    _applyEnhancements() {
        // Auto-center zoom on the segmented person (user setting, virtual background only).
        if (this._options?.virtualBackground?.autoCenterZoom) {
            this._applyAutoCenterZoom();
        }

        // Apply video enhancements if enabled
        if (this._qualitySettings.enableVideoEnhancer) {
            this._applyVideoEnhancements();
        }
    }

    /**
     * Bounding box of the segmented subject, read from the alpha channel of the
     * current segmentation mask. The mask has already been pruned to the single
     * largest connected component (nearest person), so this is that person's box.
     *
     * @private
     * @returns {?{ x: number, y: number, width: number, height: number }} Normalised
     * [0..1] box, or null when no foreground is present.
     */
    _computeSubjectBounds() {
        const mask = this._segmentationMask;

        if (!mask) {
            return null;
        }

        const { width, height } = this._options;
        const data = mask.data;
        const threshold = 128;

        let minX = width;
        let minY = height;
        let maxX = -1;
        let maxY = -1;

        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                if (data[(((y * width) + x) * 4) + 3] >= threshold) {
                    if (x < minX) {
                        minX = x;
                    }
                    if (x > maxX) {
                        maxX = x;
                    }
                    if (y < minY) {
                        minY = y;
                    }
                    if (y > maxY) {
                        maxY = y;
                    }
                }
            }
        }

        if (maxX < minX || maxY < minY) {
            return null;
        }

        return {
            x: minX / width,
            y: minY / height,
            width: (maxX - minX + 1) / width,
            height: (maxY - minY + 1) / height
        };
    }

    /**
     * Crops the composited output to a window centred on the subject and scales it
     * back to fill the frame, so the person sits centre and larger. The target
     * crop (centre + zoom) is smoothed with an EMA to stop the frame jittering as
     * the mask wobbles, and it falls back to no crop whenever no subject is found.
     *
     * Uses an explicit source-rect drawImage rather than ctx.setTransform: a left
     * over transform would corrupt the next frame's compositing in runPostProcessing.
     *
     * @private
     * @returns {void}
     */
    _applyAutoCenterZoom() {
        const ctx = this._outputCanvasCtx;
        const canvas = this._outputCanvasElement;

        if (!ctx || !canvas.width || !canvas.height) {
            return;
        }

        const z = this._smartZoom;
        const bounds = this._computeSubjectBounds();

        let targetZoom = 1;
        let targetCX = 0.5;
        let targetCY = 0.5;

        if (bounds) {
            targetCX = bounds.x + (bounds.width / 2);
            targetCY = bounds.y + (bounds.height / 2);

            const span = Math.max(bounds.width, bounds.height, 1e-3);

            targetZoom = Math.min(z.maxZoom, Math.max(1, z.targetFill / span));
        }

        // Smooth centre and zoom towards their targets.
        z.currentZoom += (targetZoom - z.currentZoom) * z.smoothness;
        z.centerX += (targetCX - z.centerX) * z.smoothness;
        z.centerY += (targetCY - z.centerY) * z.smoothness;

        if (z.currentZoom <= 1 + CROP_EPSILON) {
            return;
        }

        const cw = canvas.width;
        const ch = canvas.height;
        const sw = cw / z.currentZoom;
        const sh = ch / z.currentZoom;

        // Centre the crop on the subject, clamped so it never leaves the frame.
        const sx = Math.max(0, Math.min(cw - sw, (z.centerX * cw) - (sw / 2)));
        const sy = Math.max(0, Math.min(ch - sh, (z.centerY * ch) - (sh / 2)));

        if (!this._zoomScratchCanvas) {
            this._zoomScratchCanvas = document.createElement('canvas');
        }
        if (this._zoomScratchCanvas.width !== cw || this._zoomScratchCanvas.height !== ch) {
            this._zoomScratchCanvas.width = cw;
            this._zoomScratchCanvas.height = ch;
            this._zoomScratchCtx = this._zoomScratchCanvas.getContext('2d');
        }

        // Hold the full composite, then redraw the crop scaled to fill the output.
        this._zoomScratchCtx.clearRect(0, 0, cw, ch);
        this._zoomScratchCtx.drawImage(canvas, 0, 0);

        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.globalCompositeOperation = 'copy';
        ctx.filter = 'none';
        ctx.drawImage(this._zoomScratchCanvas, sx, sy, sw, sh, 0, 0, cw, ch);
        ctx.restore();
    }

    /**
     * Applies video enhancements such as brightness, contrast, and saturation.
     * Improves user appearance and overall video quality.
     *
     * @private
     * @returns {void}
     */
    _applyVideoEnhancements() {
        if (!this._videoEnhancer.enabled || !this._outputCanvasCtx) {
            return;
        }

        // Apply enhancements using canvas filter property
        const enhancer = this._videoEnhancer;

        this._outputCanvasCtx.filter = `
            brightness(${1 + enhancer.brightness})
            contrast(${1 + enhancer.contrast})
            saturate(${enhancer.saturation})
            blur(${enhancer.noiseReduction}px)
        `;

        // Redraw with filters applied
        this._outputCanvasCtx.drawImage(
            this._outputCanvasElement, 0, 0,
            this._outputCanvasElement.width,
            this._outputCanvasElement.height
        );

        // Reset filter for next frame
        this._outputCanvasCtx.filter = 'none';
    }

    /**
     * Checks if the local track supports this effect.
     *
     * @param {Object} mhLocalTrack - Track to apply effect.
     * @returns {boolean} - Returns true if this effect can run on the specified track
     * false otherwise.
     */
    isEnabled(mhLocalTrack) {
        return mhLocalTrack.isVideoTrack() && mhLocalTrack.videoType === 'camera';
    }

    /**
     * Starts loop to capture video frame and render the segmentation mask.
     *
     * @param {MediaStream} stream - Stream to be used for processing.
     * @returns {MediaStream} - The stream with the applied effect.
     */
    startEffect(stream) {
        this._stream = stream;
        this._running = true;
        this._maskFrameTimerWorker = new Worker(timerWorkerScript, { name: 'Blur effect worker' });
        this._maskFrameTimerWorker.onmessage = this._onMaskFrameTimer;

        const firstVideoTrack = stream.getVideoTracks()[0];
        const settings = firstVideoTrack.getSettings ? firstVideoTrack.getSettings() : firstVideoTrack.getConstraints();
        const width = Number(settings?.width) || stream.width || 640;
        const height = Number(settings?.height) || stream.height || 360;
        const frameRate = Number(settings?.frameRate) || 30;

        this._segmentationMask = new ImageData(this._options.width, this._options.height);
        this._segmentationMaskCanvas = document.createElement('canvas');
        this._segmentationMaskCanvas.width = this._options.width;
        this._segmentationMaskCanvas.height = this._options.height;
        this._segmentationMaskCtx = this._segmentationMaskCanvas.getContext('2d');

        this._outputCanvasElement.width = width;
        this._outputCanvasElement.height = height;
        this._outputCanvasCtx = this._outputCanvasElement.getContext('2d');

        this._inputVideoElement.width = width;
        this._inputVideoElement.height = height;
        this._inputVideoElement.autoplay = true;
        this._inputVideoElement.srcObject = stream;

        const timerWorker = this._maskFrameTimerWorker;

        this._inputVideoElement.onloadeddata = () => {
            // The effect may already be stopped (or restarted on another stream) by the time the video has data:
            // a new camera gets one effect when it is created and another as soon as it reaches the store, so the
            // first is stopped almost at once. Starting the monitors here then would leave them running for good -
            // stopEffect() has already disposed of them - each running a CPU benchmark on the main thread every 30s.
            if (!this._running || this._maskFrameTimerWorker !== timerWorker) {
                return;
            }

            timerWorker.postMessage({
                id: SET_TIMEOUT,
                timeMs: 1000 / 30
            });

            // Start dynamic resource and battery monitoring (Phase 2 & 3)
            try {
                if (this._resourceMonitor) {
                    this._resourceMonitor.startMonitoring();
                }
                if (this._batteryMonitor) {
                    this._batteryMonitor.startMonitoring();
                }
            } catch (error) {
                logger.warn('[VirtualBackground] Error starting monitors:', error);
            }
        };

        const outputStream = this._outputCanvasElement.captureStream(frameRate);
        const outputTrack = outputStream.getVideoTracks()[0];

        if (!outputTrack) {
            throw new Error('Virtual background did not produce a video track');
        }

        return outputStream;
    }

    /**
     * Stops the capture and render loop.
     *
     * @returns {void}
     */
    stopEffect() {
        this._running = false;
        this._maskFrameTimerWorker.postMessage({ id: CLEAR_TIMEOUT });
        this._maskFrameTimerWorker.terminate();

        // Drop the per-frame scratch buffers. Without this a session that turns a background on once
        // keeps the blur canvas, the previous mask and the video/canvas elements alive for as long as the
        // effect object is referenced anywhere - and toggling backgrounds constructs a new effect each
        // time, so they accumulate.
        disposeMaskBlurScratch();
        this._prevMaskImageData = undefined;
        this._segmentationMask = undefined;
        this._segmentationMaskCanvas = undefined;
        this._segmentationMaskCtx = undefined;
        this._guideImageData = undefined;
        this._zoomScratchCanvas = undefined;
        this._zoomScratchCtx = undefined;

        if (this._inputVideoElement) {
            this._inputVideoElement.srcObject = null;
        }

        // Stop monitoring resources and battery
        if (this._resourceMonitor) {
            this._resourceMonitor.dispose();
        }
        if (this._batteryMonitor) {
            this._batteryMonitor.dispose();
        }
    }

    /**
     * Handle resource capability change (Phase 2)
     * Called when available resources change significantly
     *
     * @private
     * @param {Object} data - Change data
     * @param {string} data.newCapability - New detected capability tier
     * @param {Object} data.qualitySettings - New quality settings
     * @returns {void}
     */
    _onResourceCapabilityChange(data) {
        try {
            const { newCapability, qualitySettings } = data;

            logger.info('[VirtualBackground] Resource-based capability change:', {
                from: this._capabilityUsed,
                to: newCapability
            });

            this._capabilityUsed = newCapability;
            this._qualitySettings = qualitySettings;

            // Update rendering scale and other visual settings
            this._updateQualitySettings(qualitySettings);
        } catch (error) {
            logger.error('[VirtualBackground] Error handling resource capability change:', error);
        }
    }

    /**
     * Handle battery status adaptation (Phase 3)
     * Called when battery level changes critically
     *
     * @private
     * @param {Object} data - Battery adaptation data
     * @param {string} data.adaptation - Type of adaptation ('low-battery' or 'restored')
     * @param {string} data.capability - New capability or null if restoring
     * @param {Object} data.qualitySettings - New quality settings or null if restoring
     * @param {string} data.reason - Reason for adaptation
     * @returns {void}
     */
    _onBatteryAdapt(data) {
        try {
            const { adaptation, capability, qualitySettings, reason } = data;

            logger.info('[VirtualBackground] Battery adaptation:', {
                adaptation,
                capability,
                reason
            });

            if (adaptation === 'low-battery' && capability && qualitySettings) {
                // Apply low-battery settings
                this._capabilityUsed = capability;
                this._qualitySettings = qualitySettings;
                this._updateQualitySettings(qualitySettings);
            } else if (adaptation === 'restored') {
                // Restore to normal operation (let resource monitor re-evaluate)
                const normalCapability = this._deviceCapability || 'medium';
                const normalSettings = getQualitySettings(normalCapability);

                this._capabilityUsed = normalCapability;
                this._qualitySettings = normalSettings;
                this._updateQualitySettings(normalSettings);
            }
        } catch (error) {
            logger.error('[VirtualBackground] Error handling battery adaptation:', error);
        }
    }

    /**
     * Update quality settings during runtime
     *
     * @private
     * @param {Object} settings - New quality settings
     * @returns {void}
     */
    _updateQualitySettings(settings) {
        if (!settings) {
            return;
        }

        // Update smart zoom settings
        if (typeof settings.enableSmartZoom !== 'undefined') {
            this._smartZoom.enabled = settings.enableSmartZoom;
        }

        // Update video enhancer settings
        if (typeof settings.enableVideoEnhancer !== 'undefined') {
            this._videoEnhancer.enabled = settings.enableVideoEnhancer;
        }

        // Update other settings as needed
        if (typeof settings.targetFps !== 'undefined') {
            this._currentTargetFps = settings.targetFps;
            logger.debug('[VirtualBackground] Updated targetFps:', settings.targetFps);
        }

        logger.debug('[VirtualBackground] Quality settings updated');
    }

}

/* =======================================================================
 *                  MASK / MATTING UTILITIES
 * ======================================================================= */

/**
 * Guided filter on alpha mask using RGB guidance image.
 * Approximates a joint bilateral filter:
 *   - weights depend on spatial distance (sigmaSpatial)
 *   - and luminance similarity (sigmaRange).
 *
 * This keeps edges aligned with the underlying person (hands, shirt).
 */

/**
 * Computes bilateral filter weight accumulation for a single pixel.
 */
function _computeBilateralWeight({ y, x, width, height, radius, maskData, guideData, lumC, sigmaS, sigmaR, gaussian }) {
    let sumW = 0;
    let sumA = 0;

    for (let dy = -radius; dy <= radius; dy++) {
        const ny = y + dy;

        if (ny >= 0 && ny < height) {
            for (let dx = -radius; dx <= radius; dx++) {
                const nx = x + dx;

                if (nx >= 0 && nx < width) {
                    const nIdx = ((ny * width) + nx) * 4;

                    const aN = maskData[nIdx + 3] / 255;

                    const rN = guideData[nIdx];
                    const gN = guideData[nIdx + 1];
                    const bN = guideData[nIdx + 2];
                    const lumN = ((0.299 * rN) + (0.587 * gN) + (0.114 * bN)) / 255;

                    const ds = Math.sqrt((dx * dx) + (dy * dy));
                    const dr = lumC - lumN;

                    const wS = gaussian(ds, sigmaS);
                    const wR = gaussian(dr, sigmaR);
                    const w = wS * wR;

                    sumW += w;
                    sumA += w * aN;
                }
            }
        }
    }

    return { sumW,
        sumA };
}

/**
 * Description.
 */
function guidedFilterMask({ maskImage, guideImage, width, height, radius = 2, sigmaSpatial = 2.0, sigmaRange = 0.1 }) {
    const maskData = maskImage.data;
    const guideData = guideImage.data;

    const out = new Uint8ClampedArray(maskData.length);

    const gaussian = (x, s) => Math.exp(-(x * x) / (2 * s * s));
    const sigmaS = sigmaSpatial;
    const sigmaR = sigmaRange;

    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            let sumW = 0;
            let sumA = 0;

            const idx = ((y * width) + x) * 4;
            const aCenter = maskData[idx + 3] / 255;

            // Luminance of center pixel in guide
            const rC = guideData[idx];
            const gC = guideData[idx + 1];
            const bC = guideData[idx + 2];
            const lumC = ((0.299 * rC) + (0.587 * gC) + (0.114 * bC)) / 255;

            const result = _computeBilateralWeight({
                y,
                x,
                width,
                height,
                radius,
                maskData,
                guideData,
                lumC,
                sigmaS,
                sigmaR,
                gaussian
            });

            sumW = result.sumW;
            sumA = result.sumA;

            const aOut = sumW > 0 ? sumA / sumW : aCenter;

            out[idx + 3] = Math.max(0, Math.min(255, Math.round(aOut * 255)));
        }
    }

    return new ImageData(out, width, height);
}

/**
 * Blur mask edges using canvas filter blur.
 * This is a small-radius blur (~2px) to soften stair-stepping
 * without making the matte too soft.
 */
/**
 * Scratch canvas for {@link blurMaskEdges}, created once and reused.
 *
 * This used to be allocated per call, and blurMaskEdges runs on every rendered frame - so at 30fps it was
 * creating and discarding 30 GPU-backed canvases and their 2d contexts per second, on top of the ImageData
 * each one produced. That is the dominant source of the GC sawtooth behind the reported stutter: the
 * pauses land in the middle of the render loop, so frames are composited late and the video looks slow
 * even though inference itself keeps up. The mask dimensions are fixed for the life of an effect, so one
 * canvas serves every frame.
 */
let blurCanvas;
let blurCtx;

/**
 * Releases the shared blur scratch canvas. Called when an effect stops so a session that never uses a
 * virtual background again does not hold a canvas alive.
 *
 * @returns {void}
 */
export function disposeMaskBlurScratch() {
    blurCanvas = undefined;
    blurCtx = undefined;
}

/**
 * Blurs the edges of the alpha mask using a canvas filter.
 *
 * @param {ImageData} maskImage - The alpha mask image data.
 * @param {number} width - The width of the mask.
 * @param {number} height - The height of the mask.
 * @param {number} blurPx - The amount of blur to apply.
 * @returns {ImageData} - The blurred mask image data.
 */
function blurMaskEdges(maskImage, width, height, blurPx = 2) {
    if (!blurCanvas || blurCanvas.width !== width || blurCanvas.height !== height) {
        blurCanvas = new OffscreenCanvas(width, height);
        blurCtx = blurCanvas.getContext('2d', { willReadFrequently: true });
    }

    const ctx = blurCtx;

    // The canvas is reused, so it still holds the previous frame's mask; the blur below samples
    // neighbouring pixels and would otherwise bleed that stale alpha into this frame's edges.
    ctx.filter = 'none';
    ctx.clearRect(0, 0, width, height);

    ctx.putImageData(maskImage, 0, 0);
    ctx.filter = `blur(${blurPx}px)`;
    const blurred = ctx.getImageData(0, 0, width, height);

    const out = new ImageData(width, height);

    for (let i = 0; i < width * height; i++) {
        out.data[(i * 4) + 3] = blurred.data[(i * 4) + 3];
    }

    return out;
}

/**
 * Keeps only the single largest connected foreground region in the alpha mask and
 * zeroes every other region. Used to drop people standing behind the main subject:
 * the model segments everyone in frame, but the person closest to the camera fills
 * the most pixels, so the largest blob is the one to keep.
 *
 * Two-pass connected-component labelling with union-find, 8-connectivity so thin
 * links (a raised arm, hair) stay attached to the body. Operates in place on the
 * alpha channel of {@code mask}.
 *
 * @param {ImageData} mask - Alpha mask to prune in place.
 * @param {number} width - Mask width.
 * @param {number} height - Mask height.
 * @param {number} [threshold=128] - Alpha (0-255) above which a pixel is foreground.
 * @returns {void}
 */
function keepLargestConnectedComponent(mask, width, height, threshold = 128) {
    const data = mask.data;
    const n = width * height;
    const labels = new Int32Array(n);
    const parent = [ 0 ]; // parent[0] unused; labels start at 1.
    let nextLabel = 1;

    const find = x => {
        let root = x;

        while (parent[root] !== root) {
            root = parent[root];
        }

        // Path compression.
        let node = x;

        while (parent[node] !== root) {
            const next = parent[node];

            parent[node] = root;
            node = next;
        }

        return root;
    };
    const union = (a, b) => {
        const ra = find(a);
        const rb = find(b);

        if (ra !== rb) {
            parent[Math.max(ra, rb)] = Math.min(ra, rb);
        }
    };

    // First pass: provisional labels + equivalences.
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const i = (y * width) + x;

            if (data[(i * 4) + 3] < threshold) {
                // Background pixel - leave it unlabelled.

                continue;
            }

            // Already-scanned neighbours: left, up-left, up, up-right. Read inline
            // (no array allocation) - this runs on every foreground pixel of every
            // frame, so a literal here would churn the GC.
            const left = x > 0 ? labels[i - 1] : 0;
            const up = y > 0 ? labels[i - width] : 0;
            const upLeft = x > 0 && y > 0 ? labels[i - width - 1] : 0;
            const upRight = x < width - 1 && y > 0 ? labels[i - width + 1] : 0;

            let min = 0;

            if (left && (min === 0 || left < min)) {
                min = left;
            }
            if (up && (min === 0 || up < min)) {
                min = up;
            }
            if (upLeft && (min === 0 || upLeft < min)) {
                min = upLeft;
            }
            if (upRight && (min === 0 || upRight < min)) {
                min = upRight;
            }

            if (min === 0) {
                labels[i] = nextLabel;
                parent[nextLabel] = nextLabel;
                nextLabel++;
            } else {
                labels[i] = min;
                if (left) {
                    union(min, left);
                }
                if (up) {
                    union(min, up);
                }
                if (upLeft) {
                    union(min, upLeft);
                }
                if (upRight) {
                    union(min, upRight);
                }
            }
        }
    }

    // Second pass: resolve roots, accumulate areas, find the largest.
    const area = new Int32Array(nextLabel);
    let bestRoot = 0;
    let bestArea = 0;

    for (let i = 0; i < n; i++) {
        if (!labels[i]) {

            continue;
        }
        const root = find(labels[i]);

        labels[i] = root;
        const a = ++area[root];

        if (a > bestArea) {
            bestArea = a;
            bestRoot = root;
        }
    }

    if (!bestRoot) {
        return;
    }

    // Drop every foreground pixel that is not part of the largest component.
    for (let i = 0; i < n; i++) {
        if (labels[i] !== bestRoot) {
            data[(i * 4) + 3] = 0;
        }
    }
}

/**
 * Temporal mask smoothing: EMA over alpha channel.
 * mask_t = alpha * prev + (1 - alpha) * current
 */
function smoothMaskOverTime(currentMask, prevMask, alpha = 0.65) {
    if (!prevMask) {
        return currentMask;
    }

    const cur = currentMask.data;
    const prev = prevMask.data;
    const out = new Uint8ClampedArray(cur.length);

    for (let i = 0; i < cur.length; i += 4) {
        const aPrev = prev[i + 3] / 255;
        const aCur = cur[i + 3] / 255;
        const aSm = (alpha * aPrev) + ((1 - alpha) * aCur);

        out[i + 3] = Math.round(aSm * 255);
    }

    return new ImageData(out, currentMask.width, currentMask.height);
}
